import React, { useState, useEffect, createContext, useContext } from "react";
import "./App.css";
import "./AdminStyles.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Header from "./components/Header";
import VideoGrid from "./components/VideoGrid";
import VideoPage from "./components/VideoPage";
import AdminLogin from "./components/admin/AdminLogin";
import AdminPanel from "./components/AdminPanel";
import BottomNav from "./components/BottomNav";
import CategoryOverlay from "./components/CategoryOverlay";
import StoryList from "./components/StoryList";
import PageView from "./components/PageView";
import WebSidebar from "./components/WebSidebar";
import Footer from "./components/Footer";
import { LangProvider } from './components/LangToggle';
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Site context for settings + theme
const SiteContext = createContext({});
export const useSite = () => useContext(SiteContext);

const SiteProvider = ({ children }) => {
  const [settings, setSettings] = useState({});
  const [theme, setTheme] = useState({});
  const [maintenance, setMaintenance] = useState(null);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/settings/public`).then(r => setSettings(r.data || {})).catch(() => {});
    axios.get(`${BACKEND_URL}/api/system/public`).then(r => {
      if (r.data && r.data.maintenance_mode) setMaintenance(r.data.maintenance_message || 'Site under maintenance');
      else setMaintenance(false);
    }).catch(() => setMaintenance(false));
    axios.get(`${BACKEND_URL}/api/theme/public`).then(r => {
      const t = r.data || {};
      setTheme(t);
      // Apply theme colors as CSS variables
      const root = document.documentElement;
      if (t.primary_color) root.style.setProperty('--accent-1', t.primary_color);
      if (t.secondary_color) root.style.setProperty('--accent-2', t.secondary_color);
      if (t.background_color) root.style.setProperty('--bg', t.background_color);
      if (t.text_color) root.style.setProperty('--text', t.text_color);
      if (t.font_family) root.style.setProperty('font-family', `'${t.font_family}', sans-serif`);
      // Update favicon
      if (t.favicon_url) {
        // 32x32 favicon
        let fav32 = document.querySelector("link[rel='icon'][sizes='32x32']");
        if (!fav32) { fav32 = document.createElement('link'); fav32.rel = 'icon'; fav32.sizes = '32x32'; document.head.appendChild(fav32); }
        fav32.href = t.favicon_url;
        // Default favicon
        let fav = document.querySelector("link[rel='icon']:not([sizes])");
        if (!fav) { fav = document.createElement('link'); fav.rel = 'icon'; document.head.appendChild(fav); }
        fav.href = t.favicon_url;
        fav.type = 'image/png';
        // Apple touch icon (192px)
        let favApple = document.querySelector("link[rel='apple-touch-icon']");
        if (!favApple) { favApple = document.createElement('link'); favApple.rel = 'apple-touch-icon'; favApple.sizes = '180x180'; document.head.appendChild(favApple); }
        favApple.href = t.favicon_url;
      }
      // Update logo
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (settings.meta_title) document.title = settings.meta_title;
    if (settings.meta_description) {
      let meta = document.querySelector('meta[name="description"]');
      if (meta) meta.setAttribute('content', settings.meta_description);
    }
    if (settings.meta_keywords) {
      let meta = document.querySelector('meta[name="keywords"]');
      if (meta) meta.setAttribute('content', settings.meta_keywords);
    }
  }, [settings]);

  if (maintenance && !window.location.pathname.startsWith('/admin')) {
    return (
      <div style={{background:'#08080e',color:'#e8e8f0',display:'flex',alignItems:'center',justifyContent:'center',minHeight:'100vh',fontFamily:'Inter,sans-serif',textAlign:'center',padding:20}}>
        <div>
          <div style={{fontSize:64,marginBottom:16,opacity:0.3}}>🔧</div>
          <h1 style={{fontSize:24,fontWeight:800,marginBottom:8,background:'linear-gradient(135deg,#ff3c3c,#ff8a00)',WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent'}}>{maintenance}</h1>
          <p style={{color:'#666',fontSize:14}}>Silakan kembali lagi nanti</p>
        </div>
      </div>
    );
  }
  if (maintenance === null) return null;
  return <SiteContext.Provider value={{ settings, theme }}>{children}</SiteContext.Provider>;
};

const Home = () => {
  const { settings } = useSite();
  const getInitialTab = () => {
    const params = new URLSearchParams(window.location.search);
    return params.get('tab') || 'best';
  };
  const getInitialVideo = () => {
    const params = new URLSearchParams(window.location.search);
    return params.get('video') || null;
  };

  const [activeTab, setActiveTab] = useState(getInitialTab);
  const [searchQuery, setSearchQuery] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [catOverlay, setCatOverlay] = useState(false);
  const [loadingVideo, setLoadingVideo] = useState(false);

  useEffect(() => {
    const videoId = getInitialVideo();
    if (videoId) {
      setLoadingVideo(true);
      axios.get(`${BACKEND_URL}/api/videos/${videoId}`)
        .then(r => { setSelectedVideo(r.data); setLoadingVideo(false); })
        .catch(() => { setLoadingVideo(false); updateURL(activeTab, null); });
    }
  }, []);

  const updateURL = (tab, videoId) => {
    const params = new URLSearchParams();
    if (tab && tab !== 'best') params.set('tab', tab);
    if (videoId) params.set('video', videoId);
    const query = params.toString();
    window.history.replaceState(null, '', query ? `/?${query}` : '/');
  };

  const handleSearch = (query) => setAppliedSearch(query);

  const handleVideoClick = (video) => {
    setSelectedVideo(video);
    updateURL(activeTab, video.id);
    window.scrollTo(0, 0);
  };

  const handleBack = () => { document.title = "Raja Bokep - Nonton Video Bokep Gratis HD";
    setSelectedVideo(null);
    updateURL(activeTab, null);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSelectedVideo(null);
    updateURL(tab, null);
  };

  const handleCategorySelect = (cat) => {
    setBottomTab('categories');
    handleTabChange(cat.toLowerCase());
    setAppliedSearch('');
  };


  // Content Protection
  useEffect(() => {
    if (window.location.pathname.startsWith('/admin')) return;
    // Block right-click
    const handleContext = (e) => e.preventDefault();
    // Block keyboard shortcuts
    const handleKeydown = (e) => {
      if (e.key === 'F12' || 
          (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'J' || e.key === 'C')) ||
          (e.ctrlKey && e.key === 'u') || (e.ctrlKey && e.key === 's') ||
          (e.ctrlKey && e.key === 'a') || (e.ctrlKey && e.key === 'c') ||
          (e.ctrlKey && e.key === 'p')) {
        e.preventDefault();
      }
    };
    // Block copy
    const handleCopy = (e) => { e.preventDefault(); return false; };
    // Block cut
    const handleCut = (e) => { e.preventDefault(); return false; };
    // Block select
    const handleSelect = (e) => { if (e.target.tagName !== 'INPUT' && e.target.tagName !== 'TEXTAREA') { window.getSelection().removeAllRanges(); }};
    // Block drag
    const handleDrag = (e) => e.preventDefault();
    // Block print screen
    const handleVisibility = () => { if (document.hidden) { document.title = 'Raja Bokep'; }};
    
    document.addEventListener('contextmenu', handleContext);
    document.addEventListener('keydown', handleKeydown);
    document.addEventListener('copy', handleCopy);
    document.addEventListener('cut', handleCut);
    document.addEventListener('selectstart', handleSelect);
    document.addEventListener('dragstart', handleDrag);
    document.addEventListener('visibilitychange', handleVisibility);
    
    // Anti-DevTools detection
    const devToolsCheck = setInterval(() => {
      const threshold = 160;
      if (window.outerWidth - window.innerWidth > threshold || window.outerHeight - window.innerHeight > threshold) {
        document.body.innerHTML = '<div style="display:flex;align-items:center;justify-content:center;height:100vh;background:#08080e;color:#ff3c3c;font-family:Inter,sans-serif;text-align:center"><div><h1>⛔ Access Denied</h1><p style=\"color:#666\">Developer tools detected. Please close them.</p></div></div>';
      }
    }, 1000);

    return () => {
      document.removeEventListener('contextmenu', handleContext);
      document.removeEventListener('keydown', handleKeydown);
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('cut', handleCut);
      document.removeEventListener('selectstart', handleSelect);
      document.removeEventListener('dragstart', handleDrag);
      document.removeEventListener('visibilitychange', handleVisibility);
      clearInterval(devToolsCheck);
    };
  }, []);

  if (loadingVideo) {
    return <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100vh',background:'var(--bg)',color:'#555'}}>Loading...</div>;
  }

  return (
    <div className="app-container">
      <Header
        activeTab={activeTab}
        setActiveTab={handleTabChange}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        onSearch={handleSearch}
      />
      <div className="content-wrapper">
        <div className="main-layout">
          <div className="main-content">
            {activeTab === "cerita" ? (
              <StoryList />
            ) : selectedVideo ? (
              <VideoPage video={selectedVideo} onBack={handleBack} />
            ) : (
              <VideoGrid onVideoClick={handleVideoClick} searchQuery={appliedSearch} activeTab={activeTab} />
            )}
          </div>
          <WebSidebar onVideoClick={handleVideoClick} />
        </div>
      </div>
      <Footer />
      <CategoryOverlay
        isOpen={catOverlay}
        onClose={() => setCatOverlay(false)}
        onSelect={handleCategorySelect}
      />
    </div>
  );
};

const VideoSlugRoute = () => {
  const slug = window.location.pathname.split("/video/")[1];
  const [video, setVideo] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  React.useEffect(() => {
    axios.get(`${BACKEND_URL}/api/videos/slug/${slug}`)
      .then(r => { setVideo(r.data); setLoading(false); })
      .catch(() => { window.location.href = "/"; });
  }, []);
  if (loading) return <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"100vh",background:"#0a0a12",color:"#555"}}>Loading...</div>;
  return <Home initialVideo={video} />;
};
const EmbedRoute = () => {
  const id = window.location.pathname.split("/embed/")[1];
  const [video, setVideo] = React.useState(null);
  React.useEffect(() => {
    axios.get(`${BACKEND_URL}/api/videos/${id}`).then(r => setVideo(r.data)).catch(() => {});
  }, []);
  if (!video) return <div style={{background:"#000",height:"100vh",display:"flex",alignItems:"center",justifyContent:"center",color:"#555"}}>Loading...</div>;
  return <div style={{background:"#000",height:"100vh"}}><video src={video.video_url} controls autoPlay playsInline controlsList="nodownload" style={{width:"100%",height:"100%",objectFit:"contain"}} /></div>;
};
const PageRoute = () => {
  const slug = window.location.pathname.split("/pages/")[1];
  return (
    <div className="app-container">
      <div style={{ padding: "20px 24px", maxWidth: 900, margin: "0 auto" }}>
        <PageView slug={slug} onBack={() => window.location.href = "/"} />
      </div>
    </div>
  );
};

const AdminRoute = () => {
  const { admin, loading } = useAuth();
  if (loading) return <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100vh',background:'#0a0a12',color:'#555'}}>Loading...</div>;
  return admin ? <AdminPanel /> : <AdminLogin />;
};

function App() {
  return (
    <AuthProvider>
      <SiteProvider><LangProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/pages/:slug" element={<PageRoute />} />
          <Route path="/embed/:id" element={<EmbedRoute />} />
          <Route path="/video/:slug" element={<VideoSlugRoute />} />
            <Route path="/admin" element={<AdminRoute />} />
          </Routes>
        </BrowserRouter>
      </LangProvider></SiteProvider>
    </AuthProvider>
  );
}

export default App;
